import { useMemo } from 'react';
import type { ModularComponent, RenderableModularComponent } from './ModularPageWrapper';
type ShopCategoryGridModule = Extract<ModularComponent, {
  type: 'shopCategoryGrid';
}>;
const getShopCategoryGridId = (index: number) => `shop-category-grid-${index + 1}`;
const isShopCategoryGridModule = (module: ModularComponent): module is ShopCategoryGridModule => module.type === 'shopCategoryGrid';

/**
 * Builds a lookup from category ID to the index of the final grid module that
 * renders that category. Used to decide which grid is allowed to show the
 * "Show More" button.
 */
const findLastGridIndexByCategory = (modules: ModularComponent[]): Map<string, number> => {
  const lastIndexByCategory = new Map<string, number>();
  modules.forEach((module, moduleIndex) => {
    if (isShopCategoryGridModule(module)) {
      // each pass overwrites the previous entry, so the map ends up holding
      // the final occurrence of each category.
      lastIndexByCategory.set(module.props.categoryId, moduleIndex);
    }
  });
  return lastIndexByCategory;
};
export const useShopCategoryGridProps = (modules: ModularComponent[]): RenderableModularComponent[] => useMemo(() => {
  // find the last grid per category so that the Show More button is only added to that one.
  const lastGridIndexByCategory = findLastGridIndexByCategory(modules);
  const previousGridIdByCategoryId = new Map<string, string>();
  let shopCategoryGridIndex = 0;
  return modules.map((module, index) => {
    if (!isShopCategoryGridModule(module)) return module;
    const {
      categoryId
    } = module.props;
    const gridId = getShopCategoryGridId(shopCategoryGridIndex);
    shopCategoryGridIndex += 1;
    const previousGridId = previousGridIdByCategoryId.get(categoryId);

    // record the grid as the previous grid for any later modules that share category.
    previousGridIdByCategoryId.set(categoryId, gridId);
    return {
      ...module,
      props: {
        ...module.props,
        gridId,
        previousGridId,
        // enable loading more only for the last grid of a category to prevent duplicate Show More buttons
        showMoreEnabled: lastGridIndexByCategory.get(categoryId) === index
      }
    };
  });
}, [modules]);